import "../styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import React from "react";
import { useAccessibility } from "../../../../Store/contexts/AccessibilityContext";
import { faCalendarLines } from "@fortawesome/pro-duotone-svg-icons";
import LoadingApp from "../../../../components/LoadingApp";
import ModalFeedBack from "../../../../components/ModalFeedBack";
import { Convite } from "../../../../interfaces/entities/SaudeBemEstar/PalestrasEventos/Convite";
import { Evento } from "../../../../interfaces/entities/SaudeBemEstar/PalestrasEventos/Evento";
import { ConfirmedInvitationCardProps } from "../../../../interfaces/props/SaudeBemEstar/PalestrasEventos/ConfirmedInvitationCardProps";
import { getInvitation, postCancelAttendance } from "../../../../services/SaudeBemEstar/PalestrasEventosService";
import { capitalize } from "../../../../utils/Capitalize";
import { sxButton, sxDivider, sxOutlinedButton, sxTextButton } from "../sxProps";
import { LocationButton } from "./LocationButton";
import ModalActionResponsive from "../../../../components/ModalActionResponsive";
import { sxContentRowText } from "./sxProps";

const getStatusColor = (evento: Evento) => {
  if (evento?.descrStatus === "NÃO VOU") return "error";
  else return "#00995D";
};

const getStatusDescricao = (evento: Evento) => {
  if (evento?.descrStatus === "NÃO VOU") return evento?.descrStatus;
  else return "Confirmado";
};

const getStatusSufix = (evento: Evento) => {
  if (evento?.descrStatus === "NÃO VOU" && evento?.dtCancelamento) return " - " + evento?.dtCancelamento;
  else return "";
};

export const ConfirmedInvitationCard: React.FC<ConfirmedInvitationCardProps> = ({ evento, reloadPage }) => {
  const [cancelAtendanceModalOpen, setCancelAtendanceModalOpen] = React.useState(false);
  const [cancelAtendanceLoading, setCancelAtendanceLoading] = React.useState(false);
  const [cancelAttendanceErrorMessage, setCancelAttendanceErrorMessage] = React.useState("");
  const [invitation, setInvitation] = React.useState<Convite | null>(null);
  const accessibility = useAccessibility();

  const closeCancelAtendanceModal = () => setCancelAtendanceModalOpen(false);
  const openCancelAtendanceModal = () => setCancelAtendanceModalOpen(true);
  const closeCancelAttendanceError = () => {
    setCancelAttendanceErrorMessage("");
    reloadPage();
  };
  const cancelAttendance = () => {
    const body = {
      idCtrlEventoUsr: evento.idCtrlEventoUsr || "",
      idCtrlProgEvento: evento.idCtrlProgEvento || "",
      idCtrlProgEventoUsr: evento.idCtrlProgEventoUsr || "",
    };
    setCancelAtendanceModalOpen(false);
    setCancelAtendanceLoading(true);
    postCancelAttendance(body)
      .catch((error) => setCancelAttendanceErrorMessage(error?.mensagem || " "))
      .finally(() => {
        setCancelAtendanceLoading(false)
        reloadPage();
      });
  };

  React.useEffect(() => {
    getConvite();
  }, []);

  const getConvite = () => {
    getInvitation({
      idCtrlEventoUsr: evento.idCtrlEventoUsr,
      idCtrlProgEvento: evento.idCtrlProgEvento,
    }).then(setInvitation);
  }

  const nomePrestador = invitation?.nomePrestador && capitalize(invitation?.nomePrestador);
  const especialidade = invitation?.especialidade && capitalize(invitation?.especialidade);
  const dataHora = [evento?.data, evento?.hora].filter(Boolean).join(" ");
  const addressValue = invitation?.evento?.complementos?.find(c => c.codigo === 'ENDER')?.valor;
  const address = addressValue ? capitalize(addressValue) : addressValue;

  return (
    <div>
      <div className="invitation-card-holder" tabIndex={-1} style={{ ...accessibility.contrastStyle, ...accessibility.contrastBorderAccessibility }}>
        <div className="palestra-card-icon-holder">
          <FontAwesomeIcon style={accessibility.contrastColorAccessibility} icon={faCalendarLines} className="fadt-file-contract" />
        </div>
        <div className="invitation-card-content">
          <Typography style={accessibility.textStyleAccessibility} sx={sxContentRowText}>
            <b>Palestra:</b> {evento?.titulo}
          </Typography>
          <Typography style={accessibility.textStyleAccessibility} sx={sxContentRowText}>
            <b>Convite enviado por:</b> {nomePrestador}
          </Typography>
          <Typography style={accessibility.textStyleAccessibility} sx={sxContentRowText}>
            <b>Especialidade:</b> {especialidade}
          </Typography>
          <Typography className="invitation-card-description" style={accessibility.textStyleAccessibility} sx={sxContentRowText}>
            <b >Descrição:</b>
            <div
              style={accessibility.textStyleAccessibility}
              className="invitation-card-description"
              dangerouslySetInnerHTML={{ __html: invitation?.evento?.descr ?? '-' }}
            />
          </Typography>
          <Divider sx={sxDivider} />
          <Typography style={accessibility.textStyleAccessibility} sx={sxContentRowText}>
            <b>Data e hora do evento:</b> {dataHora}
          </Typography>
          <Typography style={accessibility.textStyleAccessibility} sx={sxContentRowText}>
            <b>Modalidade:</b> {evento?.descrTipoEvento}
          </Typography>
          {evento?.tipoEvento === "P" && address && (
            <Typography style={accessibility.textStyleAccessibility} sx={sxContentRowText}>
              <b>Endereço:</b> {address}
            </Typography>
          )}
          <div className="invitation-card-content-row">
            <Typography style={accessibility.textStyleAccessibility} sx={sxContentRowText}>
              <b>Situação:&nbsp;</b>
            </Typography>
            <Typography style={accessibility.textStyleAccessibility} color={getStatusColor(evento)}>
              <b>{getStatusDescricao(evento)}&nbsp;</b>
            </Typography>
            <Typography style={accessibility.textStyleAccessibility}>{getStatusSufix(evento)}</Typography>
          </div>
          <div className="invitation-card-actions">
            <LocationButton evento={invitation?.evento || null} />
            <Button 
              className="invitation-card-action-button" 
              style={accessibility.buttonStyleAccessibility} 
              onClick={openCancelAtendanceModal} 
              sx={sxOutlinedButton} 
              variant="outlined">
                <Typography style={accessibility.buttonTextStyleAccessibility} sx={sxTextButton}>
                  CANCELAR PRESENÇA
                </Typography>
            </Button>
          </div>
        </div>
      </div>
      <LoadingApp showLoading={cancelAtendanceLoading} />
      <ModalActionResponsive
        showModal={cancelAtendanceModalOpen}
        handleShowModal={closeCancelAtendanceModal}
        textOutlinedButton="CANCELAR"
        textContainedButton="CONFIRMAR"
        title="Atenção"
        subTitle="Você realmente deseja cancelar a presença neste evento?"
        handleOutlinedButton={closeCancelAtendanceModal}
        handleContainedButton={cancelAttendance}
        icon="warning"
        width="578px"
      />
      <ModalFeedBack
        showButtonModal
        showModal={!!cancelAttendanceErrorMessage}
        handleButtonModal={closeCancelAttendanceError}
        handleShowModal={closeCancelAttendanceError}
        textButton="FECHAR"
        title="Erro ao cancelar presença"
        subTitle={cancelAttendanceErrorMessage}
        icon="error"
        classNameModal="modal-feedback-invitation"
      />
    </div>
  );
};
